import React, { useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../redux/store";

// Wrappers & Auth
import PublicWrapper from "../wrapper/PublicWrapper";
import AppWrapper from "../wrapper/AppWrapper";
import ProtectedRoute from "../auth/ProtectedRoute";

// Public Pages
import HomePage from "../pages/HomePage";
import AuthPage from "../pages/AuthPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import UnauthorizedPage from "../pages/UnauthorizedPage";
import NotFoundPage from "../pages/NotFoundPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import EmailVerificationPage from "../pages/EmailVerificationPage";

// Standalone Pages
import ProfilePage from "../pages/ProfilePage";
import SettingsPage from "../pages/SettingsPage";
import SchoolPage from "../pages/SchoolPage";
import StudentFeeLedgerStatementPage from "../pages/StudentFeeLedgerStatementPage";

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (userInfo) {
      const staticAuthRoutes = ["/", "/auth", "/forgot-password"];
      const isDynamicAuthRoute =
        location.pathname.startsWith("/password-reset/") ||
        location.pathname.startsWith("/email-verify/");

      if (staticAuthRoutes.includes(location.pathname) || isDynamicAuthRoute) {
        // @ts-ignore
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    }
  }, [userInfo, location.pathname, navigate, location.state?.from?.pathname]);

  return (
    <Routes>
      {/* 1. PUBLIC ROUTES */}
      <Route element={<PublicWrapper />}>
        <Route index element={<HomePage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/password-reset/:token" element={<ResetPasswordPage />} />
        <Route
          path="/email-verify/:token"
          element={<EmailVerificationPage />}
        />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
      </Route>

      {/* 2. PROTECTED APP ROUTES (AppWrapper acts as the shell) */}
      <Route
        element={
          <ProtectedRoute>
            <AppWrapper />
          </ProtectedRoute>
        }
      >
        {/* A. Standalone Pages (Rendered directly via <Outlet /> in AppWrapper) */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/school" element={<SchoolPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/student-fee-ledgers/:studentId"
          element={<StudentFeeLedgerStatementPage />}
        />

        {/* B. The Workspace Catch-All (For /dashboard, /users, /items, etc.) */}
        {/* React Router matches this, AppWrapper renders the Dynamic Workspace Tabs. */}
        <Route path="/*" element={<></>} />
      </Route>

      {/* 3. 404 Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
